import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PenTool, CheckCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import ConsentCaptureDialog from './ConsentCaptureDialog';
import { printConsentRecord } from '../lib/printConsent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

const ConsentManagement = () => {
  const [residents, setResidents] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedResidentId, setSelectedResidentId] = useState('');
  const [dialogResident, setDialogResident] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [r, rec] = await Promise.all([
        axios.get(`${API}/residents?status=current`),
        axios.get(`${API}/consent-records`),
      ]);
      setResidents(r.data); setRecords(rec.data);
    } catch (e) { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const openCapture = () => {
    const res = residents.find((r) => r.id === selectedResidentId);
    if (!res) { toast.error('Select a resident'); return; }
    setDialogResident(res); setOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="consent-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><PenTool className="h-6 w-6 text-indigo-600" /> Digital Consent & Signatures</h1>
        <p className="text-sm text-gray-600 mt-1">Capture and print signed ROI, House Rules, CoC, and Bedding Receipt forms. Signatures are also prompted automatically at intake and stored in each resident's file.</p>
      </div>

      <Card className="border-0 card-shadow">
        <CardHeader><CardTitle className="text-lg">Capture signatures</CardTitle><CardDescription>Choose a resident to sign or review their consent forms.</CardDescription></CardHeader>
        <CardContent className="flex flex-wrap gap-3 items-center">
          <Select value={selectedResidentId} onValueChange={setSelectedResidentId}>
            <SelectTrigger className="w-72" data-testid="consent-resident"><SelectValue placeholder="Select resident" /></SelectTrigger>
            <SelectContent>{residents.map((r) => <SelectItem key={r.id} value={r.id}>{r.first_name} {r.last_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={openCapture} className="bg-indigo-600 hover:bg-indigo-700" data-testid="open-capture-btn"><PenTool className="h-4 w-4 mr-1" /> Open signature capture</Button>
        </CardContent>
      </Card>

      <Card className="border-0 card-shadow">
        <CardHeader><CardTitle className="text-lg">Signed consents ({records.length})</CardTitle></CardHeader>
        <CardContent>
          {records.length === 0 ? <p className="text-gray-500 text-sm" data-testid="no-consents">No consents recorded yet.</p> : (
            <div className="space-y-2">
              {records.map((c) => (
                <div key={c.id} className="flex items-center justify-between border-b last:border-0 py-2 text-sm" data-testid={`consent-record-${c.id}`}>
                  <div><span className="font-medium">{c.resident_name}</span> — {c.document_title}</div>
                  <div className="flex items-center gap-3 text-gray-500">
                    <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />signed</Badge>
                    <span>{new Date(c.resident_signed_at).toLocaleDateString()}</span>
                    <Button size="sm" variant="ghost" onClick={() => printConsentRecord(c)} title="Print" data-testid={`print-record-${c.id}`}><Printer className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConsentCaptureDialog resident={dialogResident} open={open} onClose={() => { setOpen(false); load(); }} title="Consent & Signature Forms" />
    </div>
  );
};

export default ConsentManagement;
